/**
 * Fighter facts parsed from a Wikipedia article's wikitext: the
 * "Infobox martial artist" (record, height, reach, stance, birth date) and
 * the "Mixed martial arts record" table (recent fights). Pure parsing: no
 * network. Wikipedia content is CC BY-SA, so every use links back to the
 * article it came from.
 *
 * Wikitext is hand-written and varies a lot between articles, so every
 * field is optional and parsed defensively: a field that doesn't parse
 * cleanly is left null rather than guessed.
 */

export interface FightResultRow {
  result: 'Win' | 'Loss' | 'Draw' | 'NC';
  opponent: string | null;
  method: string | null;
  /** ISO date (YYYY-MM-DD) when it parses. */
  date: string | null;
}

export interface FighterStats {
  name: string;
  wikiTitle: string;
  wikiUrl: string;
  record: { wins: number; losses: number; draws: number; noContests: number } | null;
  winsBy: { ko: number; sub: number; dec: number } | null;
  birthDate: string | null;
  heightCm: number | null;
  reachCm: number | null;
  stance: string | null;
  /** Most recent first, at most RECENT_FIGHTS. */
  recentFights: FightResultRow[];
}

export const RECENT_FIGHTS = 5;

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** Strips links, refs, comments, simple templates and formatting to plain text. */
export function plainText(wikitext: string): string {
  let s = wikitext
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<ref[^>]*\/>/gi, '')
    .replace(/<ref[^>]*>[\s\S]*?<\/ref>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '');
  // {{sort|key|shown}} / {{sortname|First|Last}} keep the visible text.
  s = s.replace(/\{\{\s*sort\s*\|[^|}]*\|([^}]*)\}\}/gi, '$1');
  s = s.replace(/\{\{\s*sortname\s*\|([^|}]*)\|([^|}]*)[^}]*\}\}/gi, '$1 $2');
  // Drop any other template (flags, yes2/no2, etc.).
  for (let i = 0; i < 3; i++) s = s.replace(/\{\{[^{}]*\}\}/g, '');
  s = s.replace(/\[\[(?:[^\]|]*\|)?([^\]]*)\]\]/g, '$1');
  s = s.replace(/'{2,}/g, '');
  return s.replace(/\s+/g, ' ').trim();
}

const pad = (n: number) => String(n).padStart(2, '0');

function isoDate(y: number, m: number, d: number): string | null {
  if (!(y > 1900 && y < 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** Parses {{dts|2024|06|29}}, {{dts|June 29, 2024}}, {{birth date and age|1987|7|7}}, "June 29, 2024", "29 June 2024", "2024-06-29". */
export function parseDate(raw: string): string | null {
  const numbers =
    /\{\{[^}|]*\|(?:[^}|]*=[^}|]*\|)*\s*(\d{4})\s*\|\s*(\d{1,2})\s*\|\s*(\d{1,2})/.exec(
      raw,
    );
  if (numbers) return isoDate(+numbers[1], +numbers[2], +numbers[3]);
  const text = raw.replace(/\{\{\s*dts\s*\|/i, '').replace(/[{}|]/g, ' ');
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return isoDate(+iso[1], +iso[2], +iso[3]);
  const mdy = /([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/.exec(text);
  if (mdy && MONTHS.includes(mdy[1].toLowerCase())) {
    return isoDate(+mdy[3], MONTHS.indexOf(mdy[1].toLowerCase()) + 1, +mdy[2]);
  }
  const dmy = /(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/.exec(text);
  if (dmy && MONTHS.includes(dmy[2].toLowerCase())) {
    return isoDate(+dmy[3], MONTHS.indexOf(dmy[2].toLowerCase()) + 1, +dmy[1]);
  }
  return null;
}

/** The infobox's `| key = value` fields, lowercased keys, raw values. */
export function infoboxFields(wikitext: string): Map<string, string> {
  const fields = new Map<string, string>();
  const start = wikitext.search(/\{\{\s*Infobox martial artist/i);
  if (start < 0) return fields;
  // Walk to the infobox's matching closing braces.
  let depth = 0;
  let end = wikitext.length;
  for (let i = start; i < wikitext.length - 1; i++) {
    if (wikitext[i] === '{' && wikitext[i + 1] === '{') {
      depth++;
      i++;
    } else if (wikitext[i] === '}' && wikitext[i + 1] === '}') {
      depth--;
      i++;
      if (depth === 0) {
        end = i + 1;
        break;
      }
    }
  }
  const body = wikitext.slice(start, end);
  for (const line of body.split('\n')) {
    const m = /^\s*\|\s*([A-Za-z_0-9 ]+?)\s*=\s*(.*)$/.exec(line);
    if (m) fields.set(m[1].toLowerCase().replace(/\s+/g, '_'), m[2].trim());
  }
  return fields;
}

const intField = (fields: Map<string, string>, key: string): number | null => {
  const v = fields.get(key);
  if (v === undefined) return null;
  const m = /^\s*(\d+)/.exec(plainText(v));
  return m ? Number(m[1]) : null;
};

/** Height in cm from {{height|ft=6|in=4}}, {{height|m=1.93}}, {{convert|6|ft|4|in|...}}, "6 ft 4 in", "193 cm". */
export function parseLengthCm(
  raw: string | undefined,
  kind: 'height' | 'reach',
): number | null {
  if (!raw) return null;
  const s = raw.replace(/&nbsp;/g, ' ');
  let m = /\|\s*ft\s*=\s*(\d+)(?:\s*\|\s*in\s*=\s*([\d.]+))?/i.exec(s);
  if (m) return Math.round((+m[1] * 12 + (m[2] ? +m[2] : 0)) * 2.54);
  m = /\|\s*m\s*=\s*([\d.]+)/i.exec(s);
  if (m) return Math.round(+m[1] * 100);
  m = /\{\{\s*convert\s*\|\s*([\d.]+)\s*\|\s*ft\s*\|\s*([\d.]+)\s*\|\s*in/i.exec(s);
  if (m) return Math.round((+m[1] * 12 + +m[2]) * 2.54);
  m = /\{\{\s*convert\s*\|\s*([\d.]+)\s*\|\s*in\b/i.exec(s);
  if (m) return Math.round(+m[1] * 2.54);
  m = /\{\{\s*convert\s*\|\s*([\d.]+)\s*\|\s*cm\b/i.exec(s);
  if (m) return Math.round(+m[1]);
  const text = plainText(s);
  m = /(\d+)\s*ft\s*(\d+(?:\.\d+)?)?\s*in/i.exec(text);
  if (m) return Math.round((+m[1] * 12 + (m[2] ? +m[2] : 0)) * 2.54);
  m = /(\d+(?:\.\d+)?)\s*cm/i.exec(text);
  if (m) return Math.round(+m[1]);
  m = /(\d+(?:\.\d+)?)\s*in\b/i.exec(text);
  if (m) return Math.round(+m[1] * 2.54);
  // A bare number: a reach is normally given in inches.
  m = /^(\d{2}(?:\.\d+)?)$/.exec(text);
  if (m && kind === 'reach') return Math.round(+m[1] * 2.54);
  return null;
}

function resultOf(cell: string): FightResultRow['result'] | null {
  const t = plainText(cell).toLowerCase();
  if (/\bwin\b/.test(t)) return 'Win';
  if (/\bloss\b/.test(t)) return 'Loss';
  if (/\bdraw\b/.test(t)) return 'Draw';
  if (/\bnc\b|no contest/.test(t)) return 'NC';
  return null;
}

/** A wikitable cell may carry attributes: `style="..." | Win`. Keep the content. */
function cellContent(cell: string): string {
  const parts = cell.split(/\s\|\s?|(?<=")\|/);
  const last = parts[parts.length - 1];
  return parts.length > 1 && /=/.test(parts[0]) ? last : cell;
}

/** Recent fights from the "{{MMA record start}} ... {{end}}" table, most recent first. */
export function parseRecordTable(
  wikitext: string,
  limit = RECENT_FIGHTS,
): FightResultRow[] {
  const start = wikitext.search(/\{\{\s*MMA record start\s*\}\}/i);
  if (start < 0) return [];
  const rest = wikitext.slice(start);
  const endRel = rest.search(/\{\{\s*end\s*\}\}|\n\|\}/i);
  const table = endRel > 0 ? rest.slice(0, endRel) : rest;
  const rows: FightResultRow[] = [];
  for (const rawRow of table.split(/\n\|-[^\n]*/).slice(1)) {
    const cells = rawRow
      .split('\n')
      .filter((l) => l.startsWith('|') && !l.startsWith('|}'))
      .flatMap((l) => l.slice(1).split('||'))
      .map((c) => cellContent(c.trim()));
    if (cells.length < 4) continue;
    const result = resultOf(cells[0]);
    if (!result) continue;
    rows.push({
      result,
      opponent: plainText(cells[2]) || null,
      method: plainText(cells[3]) || null,
      date: cells[5] ? parseDate(cells[5]) : null,
    });
    if (rows.length >= limit) break;
  }
  return rows;
}

/** Everything this page shows about one fighter, or null when the article isn't a fighter's. */
export function parseFighterWikitext(
  name: string,
  wikiTitle: string,
  wikitext: string,
): FighterStats | null {
  const fields = infoboxFields(wikitext);
  if (fields.size === 0) return null;
  const wins = intField(fields, 'mma_win');
  const losses = intField(fields, 'mma_loss');
  const ko = intField(fields, 'mma_kowin');
  const sub = intField(fields, 'mma_subwin');
  const dec = intField(fields, 'mma_decwin');
  const stance = fields.get('stance');
  return {
    name,
    wikiTitle,
    wikiUrl: `https://en.wikipedia.org/wiki/${encodeURIComponent(wikiTitle.replace(/ /g, '_'))}`,
    record:
      wins !== null && losses !== null
        ? {
            wins,
            losses,
            draws: intField(fields, 'mma_draw') ?? 0,
            noContests: intField(fields, 'mma_nc') ?? 0,
          }
        : null,
    winsBy:
      ko !== null || sub !== null || dec !== null
        ? { ko: ko ?? 0, sub: sub ?? 0, dec: dec ?? 0 }
        : null,
    birthDate: fields.get('birth_date') ? parseDate(fields.get('birth_date')!) : null,
    heightCm: parseLengthCm(fields.get('height'), 'height'),
    reachCm: parseLengthCm(fields.get('reach'), 'reach'),
    stance: stance ? plainText(stance) || null : null,
    recentFights: parseRecordTable(wikitext),
  };
}

const normalizeName = (s: string): string =>
  s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Cache key and article-title check: same name ignoring accents, case, punctuation and a "(fighter)" suffix. */
export function fighterKey(name: string): string {
  return normalizeName(name);
}

export function titleMatchesName(title: string, name: string): boolean {
  return normalizeName(title.replace(/\s*\([^)]*\)\s*$/, '')) === normalizeName(name);
}

/** Age in whole years on `atMs`. */
export function ageOn(birthDate: string | null, atMs: number): number | null {
  if (!birthDate) return null;
  const b = new Date(`${birthDate}T00:00:00Z`);
  const at = new Date(atMs);
  let age = at.getUTCFullYear() - b.getUTCFullYear();
  const beforeBirthday =
    at.getUTCMonth() < b.getUTCMonth() ||
    (at.getUTCMonth() === b.getUTCMonth() && at.getUTCDate() < b.getUTCDate());
  if (beforeBirthday) age--;
  return age >= 0 && age < 100 ? age : null;
}
